import _get from 'lodash/get';

import { getSteps, getStepsFeatureMap, getVisibleSteps } from '../utils';

/**
 * @param {typeof import("../stubs").DEFAULT_TODOS} todos
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 */
export function getOnboardingStatus(
  todos = {},
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const visibleSteps = getVisibleSteps(isAdvancedMode, isAdvancedSupported);
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );

  const firstStepId = visibleSteps[0].id;
  const lastStepId = visibleSteps[visibleSteps.length - 1].id;
  const firstFeature = _get(stepsFeatureMap, firstStepId, [])[0];
  const lastFeature = _get(stepsFeatureMap, lastStepId, []).slice(-1)[0];
  const isStarted = _get(todos, firstFeature, false);
  const isCompleted = _get(todos, lastFeature, false);
  const isInProgress = isStarted && !isCompleted;
  return { isStarted, isInProgress, isCompleted };
}

/**
 * @param {typeof import("../stubs").DEFAULT_TODOS} todos
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 * @returns {keyof typeof import("../stubs").STEP_ID} active step id
 */
export function getActiveStep(
  todos = {},
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const steps = getSteps(isAdvancedMode, isAdvancedSupported);
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );
  const step = steps.find(step => {
    const features = stepsFeatureMap[step.id] || [];
    return features.some(feature => !_get(todos, feature, false));
  });
  return _get(step, 'id', null);
}

/**
 * @param {keyof typeof import("../stubs").STEP_ID} stepId active step id
 * @param {typeof import("../stubs").DEFAULT_TODOS} todos
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 * @returns {keyof typeof import("../stubs").DEFAULT_TODOS} active feature
 */
export function getActiveFeature(
  stepId = {},
  todos = {},
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );
  const stepFeatures = _get(stepsFeatureMap, stepId, []);
  const activeFeature = stepFeatures.find(feature => !_get(todos, feature));
  return activeFeature || _get(stepFeatures, '[0]', null);
}

/**
 * @param {keyof typeof import("../stubs").DEFAULT_TODOS} featureId
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 * @returns {boolean} whether can go back
 */
export function canGoBack(
  featureId,
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const steps = getSteps(isAdvancedMode, isAdvancedSupported);
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );
  const firstStepFirstFeature = _get(stepsFeatureMap, steps[0].id, [])[0];
  return featureId !== firstStepFirstFeature;
}

/**
 * @param {keyof typeof import("../stubs").STEP_ID} stepId current step id
 * @param {keyof typeof import("../stubs").DEFAULT_TODOS} featureId current feature id
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 * @returns {{
 *  stepId: keyof typeof import("../stubs").STEP_ID,
 *  featureId: keyof typeof import("../stubs").DEFAULT_TODOS
 * }} previous step and feature id
 */
export function getPreviousStepAndFeature(
  stepId,
  featureId,
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const steps = getSteps(isAdvancedMode, isAdvancedSupported);
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );
  const currStepIndex = steps.findIndex(step => step.id === stepId);
  const currStepsFeatures = _get(stepsFeatureMap, stepId, []);
  const currFeatureIndex = currStepsFeatures.findIndex(id => id === featureId);

  if (currStepIndex <= 0 && currFeatureIndex <= 0) {
    return { stepId, featureId };
  }

  if (currFeatureIndex > 0) {
    const previousFeature = currStepsFeatures[currFeatureIndex - 1];
    return { stepId, featureId: previousFeature };
  }

  const prevStep = steps[currStepIndex - 1];
  const prevStepFeatures = _get(stepsFeatureMap, prevStep.id, []);
  const prevStepLastFeature = prevStepFeatures[prevStepFeatures.length - 1];
  return { stepId: prevStep.id, featureId: prevStepLastFeature };
}

/**
 * @param {keyof typeof import("../stubs").STEP_ID} stepId current step id
 * @param {keyof typeof import("../stubs").DEFAULT_TODOS} featureId current feature id
 * @param {boolean} isAdvancedMode
 * @param {boolean} isAdvancedSupported
 * @returns {{
 *  stepId: keyof typeof import("../stubs").STEP_ID,
 *  featureId: keyof typeof import("../stubs").DEFAULT_TODOS
 * }} next step and feature id
 */
export function getNextStepAndFeature(
  stepId,
  featureId,
  isAdvancedMode = false,
  isAdvancedSupported = false
) {
  const steps = getSteps(isAdvancedMode, isAdvancedSupported);
  const stepsFeatureMap = getStepsFeatureMap(
    isAdvancedMode,
    isAdvancedSupported
  );
  const currStepIndex = steps.findIndex(step => step.id === stepId);
  const currStepsFeatures = _get(stepsFeatureMap, stepId, []);
  const currFeatureIndex = currStepsFeatures.findIndex(id => id === featureId);

  if (
    currStepIndex >= steps.length - 1 &&
    currFeatureIndex >= currStepsFeatures.length - 1
  ) {
    return { stepId, featureId };
  }

  if (currFeatureIndex < currStepsFeatures.length - 1) {
    const nextFeature = currStepsFeatures[currFeatureIndex + 1];
    return { stepId, featureId: nextFeature };
  }

  const nextStep = steps[currStepIndex + 1];
  const nextStepFeatures = _get(stepsFeatureMap, nextStep.id, []);
  const nextStepFirstFeature = nextStepFeatures[0];
  return { stepId: nextStep.id, featureId: nextStepFirstFeature };
}
